import React, { useEffect } from 'react';
import { Button, Form, Input } from 'antd';
import Drawer from '../../../components/AppDrawer';
import type { HostTenantListItem, UpdateHostTenantRequest } from '../../tenants/api/tenants';
import { isPrimaryContactEmailLocked } from '../utils/contactEmail';

interface EditHostTenantDrawerProps {
  tenant: HostTenantListItem | null;
  open: boolean;
  onClose: () => void;
  onSave: (tenantId: string, values: UpdateHostTenantRequest) => void;
  saving: boolean;
}

const EditHostTenantDrawer: React.FC<EditHostTenantDrawerProps> = ({
  tenant,
  open,
  onClose,
  onSave,
  saving,
}) => {
  const [form] = Form.useForm<UpdateHostTenantRequest>();
  const emailLocked = tenant ? isPrimaryContactEmailLocked(tenant) : false;

  useEffect(() => {
    if (open && tenant) {
      form.setFieldsValue({
        companyName: tenant.companyName,
        primaryContactFirstName: tenant.primaryContactFirstName,
        primaryContactLastName: tenant.primaryContactLastName,
        primaryContactEmail: tenant.primaryContactEmail,
        phone: tenant.phone,
      });
    }
  }, [open, tenant, form]);

  function handleClose() {
    form.resetFields();
    onClose();
  }

  function handleFinish(values: UpdateHostTenantRequest) {
    if (!tenant) {
      return;
    }

    onSave(tenant.id, values);
  }

  return (
    <Drawer
      title="Edit tenant"
      size="large"
      placement="right"
      open={open}
      onClose={handleClose}
      destroyOnClose
      footer={(
        <div style={{ textAlign: 'right' }}>
          <Button onClick={handleClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            Save changes
          </Button>
        </div>
      )}
    >
      <div className="cp-form-section">
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item name="companyName" label="Company name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="primaryContactFirstName" label="Primary contact first name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="primaryContactLastName" label="Primary contact last name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="primaryContactEmail"
            label="Primary contact email"
            rules={[{ required: true, type: 'email' }]}
            extra={emailLocked ? 'Email cannot be changed after the organization or contact is active.' : undefined}
          >
            <Input disabled={emailLocked} />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
};

export default EditHostTenantDrawer;
